// QR Kiosk v2 — §8.4 citation ref-tag rewrites (pure). COPY-NOT-IMPORT of the tag
// regexes + readAttr from customer/midas/components/v3/markdownComponentsV3.tsx
// (that module imports CiteV3/LiveRefV3/TelRefV3 components → must NOT enter the v2
// graph; §0 copy policy — same precedent as midasMarkdown.tsx's verbatim copy).
// SIMPLIFIED kiosk sentinel: ⟦KREF␟<page>␟<encLabel>⟧ (one chip kind — a page/
// document reference). <list-ref>/<page-ref> → chip sentinel; the no-surface tags
// (<live-ref>/<plc-ref>/<tel-ref>/<val>) are stripped to their label so nothing
// ever leaks raw markup (honest plain text, never a fake chip). Pure: no React, no
// MUI. Streaming hold-back reuses the already-v2-approved shared helper.
import { trimUnclosedRefTagTail } from "../../../shared/chatBot/streamBotMessage";

const OPEN = "⟦"; // ⟦
const CLOSE = "⟧"; // ⟧
const SEP = ""; // U+001F unit separator (same char as desktop; a REAL char)

// ── tag regexes (copied verbatim from markdownComponentsV3.tsx) ──
const LIST_REF_TAG_RE = /<list-ref\b([^>]*)>([\s\S]*?)<\/list-ref>/gi;
const PAGE_REF_PAIRED_RE = /<page-ref\b([^>]*?)>([\s\S]*?)<\/page-ref>/gi;
const PAGE_REF_VOID_RE = /<page-ref\b([^>]*?)\/?>/gi;
const LIST_DOUBLE_BRACKET_RE = /\[\[list:(\d+)\]\]/gi;
const PAGE_DOUBLE_BRACKET_RE = /\[\[page:(\d+)\]\]/gi;
// no-chip tags — stripped to their inner label / attribute (paired form)
const LIVE_REF_TAG_RE = /<live-ref\b([^>]*)>([\s\S]*?)<\/live-ref>/gi;
const PLC_REF_TAG_RE = /<plc-ref\b([^>]*)>([\s\S]*?)<\/plc-ref>/gi;
const TEL_REF_TAG_RE = /<tel-ref\b([^>]*)>([\s\S]*?)<\/tel-ref>/gi;
const VAL_TAG_RE = /<val\b([^>]*)>([\s\S]*?)<\/val>/gi;
// §7.2.3 void / self-closing forms (mirror PAGE_REF_VOID_RE) — degrade to the attr
// fallback instead of leaking raw markup that skipHtml then silently drops (data vanishes).
const LIVE_REF_VOID_RE = /<live-ref\b([^>]*?)\/?>/gi;
const PLC_REF_VOID_RE = /<plc-ref\b([^>]*?)\/?>/gi;
const TEL_REF_VOID_RE = /<tel-ref\b([^>]*?)\/?>/gi;
const VAL_VOID_RE = /<val\b([^>]*?)\/?>/gi;

// readAttr — copied verbatim (word-boundary hardened) from markdownComponentsV3.tsx.
const readAttr = (attrs: string, name: string): string | undefined => {
  const re = new RegExp(`(?:^|[\\s/])${name}\\s*=\\s*(?:"([^"]*)"|'([^']*)'|([^\\s>]+))`, "i");
  const m = attrs.match(re);
  if (!m) return undefined;
  return m[1] ?? m[2] ?? m[3];
};

const safeDecode = (s: string): string => {
  try {
    return decodeURIComponent(s);
  } catch {
    return s;
  }
};

const buildKref = (page: number, label: string): string | null => {
  if (!Number.isFinite(page) || page <= 0) return null;
  return `${OPEN}KREF${SEP}${page}${SEP}${encodeURIComponent(label || "")}${CLOSE}`;
};

const stripTag =
  (re: RegExp, ...attrFallbacks: string[]) =>
  (text: string): string =>
    text.replace(re, (_full: string, attrs: string, body: string) => {
      const inner = (body || "").trim();
      if (inner) return inner;
      for (const a of attrFallbacks) {
        const v = readAttr(attrs || "", a);
        if (v) return v;
      }
      return "";
    });

// §7.2.3 void-tag strip: single capture group (attrs only, no body) → attr fallback or "".
const stripVoidTag =
  (re: RegExp, ...attrFallbacks: string[]) =>
  (text: string): string =>
    text.replace(re, (_full: string, attrs: string) => {
      for (const a of attrFallbacks) {
        const v = readAttr(attrs || "", a);
        if (v) return v;
      }
      return "";
    });

// Strip surrounding `[…]` brackets / backticks that hug a KREF sentinel.
const stripHugging = (text: string): string => {
  let out = text.replace(new RegExp(`\\[\\s*(${OPEN}KREF${SEP}[^${CLOSE}]+${CLOSE})\\s*\\]`, "g"), "$1");
  out = out.replace(new RegExp(`\`\\s*(${OPEN}KREF${SEP}[^${CLOSE}]+${CLOSE})\\s*\``, "g"), "$1");
  return out;
};

/** Rewrite bot citation markup to kiosk KREF sentinels + strip no-surface tags to
 *  plain labels. Run BEFORE preprocessMarkdown (skipHtml stays ON downstream). */
export const rewriteKioskRefs = (text: string): string => {
  if (!text) return "";
  // <list-ref page="N">label</list-ref> → chip (keep the bot's inner label).
  let out = text.replace(LIST_REF_TAG_RE, (full: string, attrs: string, rawLabel: string) => {
    const pageStr = readAttr(attrs || "", "page");
    const page = pageStr !== undefined ? Number(pageStr) : NaN;
    return buildKref(page, (rawLabel || "").trim()) ?? full;
  });
  // <page-ref page="N">…</page-ref> paired, then void (self-closing / open-only).
  // Empty label → the chip localizes "Strana N" (kiosk keeps i18n in the component).
  out = out.replace(PAGE_REF_PAIRED_RE, (full: string, attrs: string) => {
    const pageStr = readAttr(attrs || "", "page");
    return buildKref(pageStr !== undefined ? Number(pageStr) : NaN, "") ?? full;
  });
  out = out.replace(PAGE_REF_VOID_RE, (full: string, attrs: string) => {
    const pageStr = readAttr(attrs || "", "page");
    return buildKref(pageStr !== undefined ? Number(pageStr) : NaN, "") ?? full;
  });
  out = stripHugging(out);
  out = out.replace(LIST_DOUBLE_BRACKET_RE, (full: string, num: string) => buildKref(Number(num), "") ?? full);
  out = out.replace(PAGE_DOUBLE_BRACKET_RE, (full: string, num: string) => buildKref(Number(num), "") ?? full);
  // No-chip tags → plain label (never a fake chip, never raw markup). Paired first, then
  // the void / self-closing forms (§7.2.3), then any empty inline-code husk left behind.
  out = stripTag(LIVE_REF_TAG_RE, "value", "address")(out);
  out = stripTag(PLC_REF_TAG_RE, "address", "bit")(out);
  out = stripTag(TEL_REF_TAG_RE, "signal_key")(out);
  out = stripTag(VAL_TAG_RE, "value", "unit")(out);
  out = stripVoidTag(LIVE_REF_VOID_RE, "value", "address")(out);
  out = stripVoidTag(PLC_REF_VOID_RE, "address", "bit")(out);
  out = stripVoidTag(TEL_REF_VOID_RE, "signal_key")(out);
  out = stripVoidTag(VAL_VOID_RE, "value", "unit")(out);
  out = out.replace(/`\s*`/g, " ");
  return out;
};

export type KioskSentinelPart =
  | { kind: "text"; text: string }
  | { kind: "ref"; page: number; label: string };

const KREF_RE = new RegExp(`${OPEN}KREF${SEP}(\\d+)${SEP}([^${CLOSE}]*)${CLOSE}`, "g");

/** Split a string into text runs + KREF chips (used by KioskCite.renderKioskText). */
export const splitKioskSentinels = (s: string): KioskSentinelPart[] => {
  const parts: KioskSentinelPart[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  KREF_RE.lastIndex = 0;
  while ((m = KREF_RE.exec(s)) !== null) {
    if (m.index > last) parts.push({ kind: "text", text: s.slice(last, m.index) });
    parts.push({ kind: "ref", page: Number(m[1]), label: safeDecode(m[2]) });
    last = m.index + m[0].length;
  }
  if (last < s.length) parts.push({ kind: "text", text: s.slice(last) });
  return parts;
};

/** Streaming safety — withhold a trailing half-streamed ref tag (e.g. "<list-re")
 *  so it never flashes as prose. Reuses the shared generalized hold-back (§5a). */
export const holdPartialRefTagTail = (buffer: string): { safe: string; held: string } =>
  trimUnclosedRefTagTail(buffer, ["list-ref", "page-ref", "live-ref", "plc-ref", "tel-ref", "val"]);
